/** A COMMIT SAYS HOW FAR IT HAS GOT (Tom, 2026-10-23: "Before the merge";
 *  the shape agreed with VC-D). A commit names a `progress_id` — a uuid4 per
 *  attempt — and the server sends `commit_progress` frames to the
 *  committing user's own sockets as it passes its steps:
 *
 *    0 waiting · 1 checking · 2 rebasing · 3 applying (done/total)
 *    · 4 sealing · 5 publishing, then {step: "landed", commit_id}
 *
 *  Skipped steps are jumped, at most one frame per 250 ms, and a refusal
 *  sends nothing (the answer carries it). The socket client hands every
 *  frame to `noteCommitProgress`; a commit screen reads the last one for its
 *  attempt. Counts and step names only: no record reaches the screen. */

export type CommitProgressStep = "waiting" | "checking" | "rebasing" | "applying" | "sealing" | "publishing";

export type CommitProgressEvent =
  | {
      type: "commit_progress";
      progress_id: string;
      island_id: string | null;
      step: CommitProgressStep;
      step_index: number;
      step_count: number;
      done: number | null;
      total: number | null;
    }
  | { type: "commit_progress"; progress_id: string; island_id: string | null; step: "landed"; commit_id: string | null };

/** each step, in words */
export const STEP_WORDS: Record<CommitProgressStep | "landed", string> = {
  waiting: "Waiting for another commit to finish",
  checking: "Checking your changes",
  rebasing: "Bringing your changes up to date",
  applying: "Applying your changes",
  sealing: "Recording the commit",
  publishing: "Letting everyone else know",
  landed: "Committed",
};

/** a new attempt's id: a uuid4 (the server drops anything else and sends nothing) */
export function newProgressId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** How far along: (step + done/total) / steps, 0..1; landed is 1. */
export function progressFraction(e: CommitProgressEvent | null | undefined): number {
  if (!e) return 0;
  if (e.step === "landed") return 1;
  const count = e.step_count > 0 ? e.step_count : 6;
  const within = e.total && e.total > 0 && typeof e.done === "number" ? Math.min(Math.max(e.done / e.total, 0), 1) : 0;
  return Math.min(Math.max((e.step_index + within) / count, 0), 1);
}

/** The step in words, with its count when it has one:
 *  "Applying your changes — 1,200 of 2,000". */
export function progressLine(e: CommitProgressEvent): string {
  const words = STEP_WORDS[e.step] ?? "Committing";
  if (e.step === "landed" || !e.total) return words;
  return `${words} — ${(e.done ?? 0).toLocaleString("en-GB")} of ${e.total.toLocaleString("en-GB")}`;
}

/** No frame for this long: the screen says the commit is still going. */
export const SILENCE_MS = 10_000;

// ---- the frames heard, per attempt ------------------------------------------

type Heard = { event: CommitProgressEvent; at: number };
const heard = new Map<string, Heard>();
const listeners = new Set<() => void>();
/** attempts kept: a page makes few; the oldest go first */
const KEEP = 20;

function isEvent(m: unknown): m is CommitProgressEvent {
  const e = m as Partial<CommitProgressEvent> | null;
  return !!e && e.type === "commit_progress" && typeof e.progress_id === "string" && typeof e.step === "string";
}

/** A `commit_progress` frame from the socket. The bar never goes back: a
 *  frame for an earlier step than the one heard is ignored. */
export function noteCommitProgress(m: unknown, now = Date.now()): void {
  if (!isEvent(m)) return;
  const was = heard.get(m.progress_id);
  const before = was?.event;
  if (before?.step === "landed") return;
  if (before && m.step !== "landed" && m.step_index < before.step_index) return;
  heard.delete(m.progress_id);
  heard.set(m.progress_id, { event: m, at: now });
  while (heard.size > KEEP) heard.delete(heard.keys().next().value as string);
  for (const l of Array.from(listeners)) l();
}

/** The last frame heard for `progressId`, and when. */
export function lastCommitProgress(progressId: string | null | undefined): Heard | null {
  return (progressId && heard.get(progressId)) || null;
}

export function onCommitProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether the attempt is still being heard from: a frame other than
 *  "landed" within SILENCE_MS. */
export function stillReporting(progressId: string, now = Date.now()): boolean {
  const h = heard.get(progressId);
  return !!h && h.event.step !== "landed" && now - h.at < SILENCE_MS;
}

// ---- whether this page could hear them ------------------------------------
// (item 258: reviews and Codex, 2026-10-25). The server re-sends the current
// step every 3 s, so a running commit is silent for SILENCE_MS only where
// this page cannot hear it: a socket away, or back for less than that.

/** the socket's state as last reported; unreported, it counts as open
 *  since forever (silence then reads as it always did) */
let socketOpen = true;
let connectedAt = Number.NEGATIVE_INFINITY;

/** The socket that carries the frames opened (true) or closed (false). */
export function noteProgressSocket(open: boolean, now = Date.now()): void {
  socketOpen = open;
  if (open) connectedAt = now;
}

/** Whether the attempt fell silent where this page could hear it: no frame
 *  for SILENCE_MS since `since`, the last frame and the last connect, on a
 *  socket open all that while. A socket that is away says nothing. */
export function fellSilent(progressId: string, since: number, now = Date.now()): boolean {
  const h = heard.get(progressId);
  if (!h || h.event.step === "landed" || !socketOpen) return false;
  return now - Math.max(h.at, connectedAt, since) >= SILENCE_MS;
}

/** for tests */
export function forgetCommitProgress(): void {
  heard.clear();
  socketOpen = true;
  connectedAt = Number.NEGATIVE_INFINITY;
}
